
interface SkeletonBarProps {
  className?: string;
  width?: string;
}

export function SkeletonBar({ className = "", width }: SkeletonBarProps) {
  return (
    <div
      className={`h-4 rounded bg-muted animate-pulse ${className}`}
      style={width ? { width } : undefined}
    />
  );
}

export function SkeletonDetailHeader() {
  return (
    <div className="space-y-3 animate-pulse">
      <div className="h-6 w-2/5 rounded bg-muted" />
      <div className="flex gap-4">
        <div className="h-3.5 w-24 rounded bg-muted" />
        <div className="h-3.5 w-20 rounded bg-muted" />
        <div className="h-3.5 w-28 rounded bg-muted" />
      </div>
    </div>
  );
}

export function SkeletonList({ rows = 6, className = "" }: { rows?: number; className?: string }) {
  return (
    <div className={`space-y-3 animate-pulse ${className}`}>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex items-center gap-3">
          <div className="size-4 rounded bg-muted flex-shrink-0" />
          <div
            className="h-4 rounded bg-muted"
            style={{ width: `${55 + ((i * 17) % 35)}%` }}
          />
        </div>
      ))}
    </div>
  );
}

export function SkeletonCard({ lines = 3 }: { lines?: number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-5 space-y-3 animate-pulse">
      <div className="h-5 w-1/3 rounded bg-muted" />
      {Array.from({ length: lines }).map((_, i) => (
        <div
          key={i}
          className="h-3.5 rounded bg-muted"
          style={{ width: `${65 + ((i * 23) % 30)}%` }}
        />
      ))}
    </div>
  );
}

export function SkeletonControlPanel() {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="lg:col-span-1 rounded-lg border border-border bg-card p-4">
        <div className="h-4 w-28 rounded bg-muted mb-4 animate-pulse" />
        <SkeletonList rows={8} />
      </div>
      <div className="lg:col-span-2 rounded-lg border border-border bg-card p-5">
        <div className="space-y-4 animate-pulse">
          <div className="flex items-center gap-2">
            <div className="h-5 w-16 rounded bg-muted" />
            <div className="h-5 w-24 rounded bg-muted" />
          </div>
          <div className="h-5 w-2/5 rounded bg-muted" />
          <div className="space-y-2 mt-4">
            <div className="h-3.5 w-full rounded bg-muted" />
            <div className="h-3.5 w-4/5 rounded bg-muted" />
            <div className="h-3.5 w-3/5 rounded bg-muted" />
          </div>
        </div>
      </div>
    </div>
  );
}

export function SkeletonPage() {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="h-7 w-1/4 rounded bg-muted" />
      <div className="h-4 w-2/5 rounded bg-muted" />
      <div className="rounded-lg border border-border bg-card p-5 space-y-4">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="flex items-center gap-4">
            <div className="h-4 w-1/4 rounded bg-muted" />
            <div className="h-4 w-1/6 rounded bg-muted" />
            <div className="h-4 w-1/5 rounded bg-muted" />
            <div className="h-4 w-16 rounded bg-muted" />
          </div>
        ))}
      </div>
    </div>
  );
}

export function SpokeSpinner({
  size = 36,
  className = "",
  label,
}: {
  size?: number;
  className?: string;
  label?: string;
}) {
  const spokeWidth = Math.max(2, Math.round(size * 0.1));
  const spokeHeight = Math.round(size * 0.28);

  return (
    <span
      className={`inline-flex flex-col items-center gap-2.5 text-indigo-600 dark:text-indigo-400 ${className}`}
      role="status"
      aria-live="polite"
      aria-label={label ?? "Loading"}
    >
      <span
        className="relative inline-block animate-[spin_1s_steps(12,end)_infinite] motion-reduce:animate-[spin_1.6s_linear_infinite]"
        style={{ width: size, height: size }}
        aria-hidden
      >
        {Array.from({ length: 12 }).map((_, i) => (
          <span
            key={i}
            className="absolute left-1/2 top-0"
            style={{
              width: spokeWidth,
              height: spokeHeight,
              marginLeft: -spokeWidth / 2,
              transform: `rotate(${i * 30}deg)`,
              transformOrigin: `50% ${size / 2}px`,
              opacity: 0.15 + (i / 11) * 0.85,
            }}
          >
            <span className="block size-full rounded-full bg-current" />
          </span>
        ))}
      </span>
      {label && (
        <span className="text-[12px] font-semibold tracking-wide text-[#475569] dark:text-white/70">
          {label}
        </span>
      )}
    </span>
  );
}

export function LoadingOverlay({
  label = "Loading",
  size = 36,
  className = "",
}: {
  label?: string;
  size?: number;
  className?: string;
}) {
  return (
    <div
      className={`absolute inset-0 z-20 flex items-center justify-center bg-white/55 dark:bg-[#131320]/55 backdrop-blur-[1px] cursor-progress ${className}`}
    >

      <span className="inline-flex rounded-2xl bg-white dark:bg-[#1a1a2e] px-6 py-4 shadow-lg">
        <SpokeSpinner size={size} label={label || undefined} />
      </span>
    </div>
  );
}
